import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLearner } from './useLearner';
import { useLearners } from './useLearners';

/**
 * Per-page guard that prevents URL skipping during onboarding.
 * Pass `requiredStep` = the step number this page represents (1-4).
 * The guard checks that the previous step (requiredStep - 1) has been completed.
 * Pass `null` or `undefined` to disable (e.g., for isAddingChild flows).
 */
export function useOnboardingGuard(requiredStep) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: activeLearner } = useLearner();
  // FP-LEARNER-STORE-RETIREMENT-01: gate routing on the underlying ['learners']
  // query having loaded once. isLoading is true only on the very first fetch;
  // a subsequent background refetch keeps `data` and so isLoading stays false.
  const { isLoading: learnersLoading } = useLearners();

  useEffect(() => {
    if (!requiredStep) return;
    // Wait for the initial fetch before making any routing decision, otherwise
    // a cold-start into an onboarding page bounces the user to step 1 on an
    // empty state before the draft has been loaded from the server.
    if (learnersLoading) return;

    // An active learner means onboarding is complete — this guard is only
    // meaningful for draft learners progressing through steps. Without this
    // check, the brief window between activating a learner and
    // navigate('/onboarding/welcome') causes a false redirect to step 1.
    if (activeLearner?.status === 'active') return;

    const STEP_ROUTES = [
      '/onboarding/child-profile', // step 0 → start here
      '/onboarding/buddy',         // step 1 done → here
      '/onboarding/use-case',      // step 2 done → here
      '/onboarding/confirm',       // step 3 done → here
    ];

    const currentStep = activeLearner?.status === 'draft'
      ? (activeLearner.onboarding_step || 0)
      : 0;

    if (currentStep < requiredStep - 1) {
      const redirectTo = STEP_ROUTES[Math.max(0, currentStep)] || '/onboarding/child-profile';
      if (redirectTo !== location.pathname) {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [
    requiredStep,
    activeLearner?.status,
    activeLearner?.onboarding_step,
    learnersLoading,
    location.pathname,
    navigate,
  ]);
}
